import React, { useState, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { apiService } from '../../services/api'
import {
  Folder,
  FolderOpen,
  Image,
  Upload,
  ArrowLeft,
  Building2,
  Settings,
  Clock,
  File,
  Video,
  Search,
  Plus,
  X,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  AlertTriangle,
  ChevronRight
} from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'
import LoadingSpinner from '../common/LoadingSpinner'
import ConfirmationModal from '../common/ConfirmationModal'
import toast from 'react-hot-toast'

const formatBytes = (bytes) => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

const formatShortDate = (dateString) => {
  if (!dateString) return 'Empty'
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ─── Main Component ───────────────────────────────────────────────────────────

const MediaFolders = () => {
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [mobileView, setMobileView] = useState('folders') // 'folders' | 'media'
  const [folderSearch, setFolderSearch] = useState('')
  const [deletingFolder, setDeletingFolder] = useState(null)
  const [deletingClientId, setDeletingClientId] = useState(null)
  const queryClient = useQueryClient()

  const { data: foldersData, isLoading: foldersLoading } = useQuery(
    ['media-folders'],
    () => apiService.get('/media/folders').then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  )
  const folders = foldersData?.folders || []

  const { data: orphanedData } = useQuery(
    ['media-orphaned-folders'],
    () => apiService.get('/media/orphaned-folders').then(res => res.data),
    { staleTime: 30000 }
  )
  const orphanedFolders = orphanedData?.folders || []

  const deleteOrphanedMutation = useMutation(
    (name) => apiService.delete(`/media/orphaned-folder/${encodeURIComponent(name)}`),
    {
      onSuccess: (_, name) => {
        toast.success(`Folder "${name}" deleted`)
        queryClient.invalidateQueries(['media-orphaned-folders'])
        setDeletingFolder(null)
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to delete folder')
        setDeletingFolder(null)
      }
    }
  )

  const deleteClientFolderMutation = useMutation(
    (clientId) => apiService.delete(`/media/client-folder/${clientId}`),
    {
      onSuccess: (_, clientId) => {
        toast.success('Media folder deleted')
        // Remove folder from cache directly — invalidateQueries would refetch and
        // bring the folder back (client still exists in DB with media_count = 0)
        queryClient.setQueryData(['media-folders'], (old) => {
          if (!old?.folders) return old
          return { ...old, folders: old.folders.filter(f => f.id !== clientId) }
        })
        if (selectedFolder?.id === clientId) {
          setSelectedFolder(null)
          setMobileView('folders')
        }
        setDeletingClientId(null)
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to delete folder')
        setDeletingClientId(null)
      }
    }
  )

  const pendingDelete = deletingFolder
    ? {
        label: deletingFolder,
        onConfirm: () => deleteOrphanedMutation.mutate(deletingFolder),
        isLoading: deleteOrphanedMutation.isLoading
      }
    : deletingClientId
    ? {
        label: folders.find(f => f.id === deletingClientId)?.company_name || deletingClientId,
        onConfirm: () => deleteClientFolderMutation.mutate(deletingClientId),
        isLoading: deleteClientFolderMutation.isLoading
      }
    : null

  const handleFolderSelect = (folder) => {
    setSelectedFolder(folder)
    setMobileView('media')
  }

  const filteredFolders = folders.filter(f =>
    f.company_name.toLowerCase().includes(folderSearch.toLowerCase())
  )
  const filteredOrphaned = orphanedFolders.filter(f =>
    f.name.toLowerCase().includes(folderSearch.toLowerCase())
  )

  const totalCount = folders.length + orphanedFolders.length

  return (
    <div className="pt-4">
      <ConfirmationModal
        isOpen={!!pendingDelete}
        onClose={() => { setDeletingFolder(null); setDeletingClientId(null) }}
        onConfirm={() => pendingDelete?.onConfirm()}
        isLoading={pendingDelete?.isLoading || false}
        title="Delete Media Folder"
        message={
          <>
            Permanently delete all media in{' '}
            <span className="font-medium text-neutral-900 dark:text-neutral-100">
              "{pendingDelete?.label}"
            </span>
            ? This cannot be undone.
          </>
        }
        confirmText="Delete Folder"
      />

      {/* Two-panel container */}
      <div className="flex flex-col lg:flex-row bg-white dark:bg-dark-200 rounded-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden shadow-sm" style={{ minHeight: '600px', height: 'calc(100vh - 11rem)' }}>

        {/* ── LEFT SIDEBAR ── */}
        <div className={cn(
          'lg:w-72 lg:min-w-[18rem] lg:flex-shrink-0 border-r border-neutral-200 dark:border-neutral-700 flex flex-col',
          mobileView === 'media' ? 'hidden lg:flex' : 'flex'
        )}>
          {/* Sidebar header */}
          <div className="px-4 pt-4 pb-3 border-b border-neutral-200 dark:border-neutral-700 flex-shrink-0 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Folder className="h-4 w-4 text-accent-600" />
                <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  Folders
                </span>
                {totalCount > 0 && (
                  <span className="bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 text-xs font-medium px-1.5 py-0.5 rounded-full">
                    {totalCount}
                  </span>
                )}
              </div>
              <FolderUploadModal folders={folders} compact />
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search folders..."
                value={folderSearch}
                onChange={e => setFolderSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg bg-neutral-50 dark:bg-dark-300 border border-neutral-200 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-600 transition-all"
              />
            </div>
          </div>

          {/* Folder list */}
          <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
            {foldersLoading ? (
              <div className="flex items-center justify-center py-10">
                <LoadingSpinner size="sm" />
              </div>
            ) : filteredFolders.length === 0 && filteredOrphaned.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                <FolderOpen className="h-10 w-10 text-neutral-300 dark:text-neutral-600 mb-2" />
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {folderSearch ? 'No folders match your search' : 'No folders yet'}
                </p>
              </div>
            ) : (
              <>
                {filteredFolders.map(folder => (
                  <FolderSidebarItem
                    key={folder.id ?? 'admin'}
                    folder={folder}
                    isSelected={
                      selectedFolder != null &&
                      (folder.is_admin
                        ? selectedFolder.is_admin === true
                        : selectedFolder.id === folder.id)
                    }
                    onClick={() => handleFolderSelect(folder)}
                    onDelete={!folder.is_admin ? () => setDeletingClientId(folder.id) : null}
                  />
                ))}

                {filteredOrphaned.length > 0 && (
                  <>
                    {filteredFolders.length > 0 && (
                      <div className="px-2 pt-3 pb-1">
                        <p className="text-[10px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">
                          Orphaned
                        </p>
                      </div>
                    )}
                    {filteredOrphaned.map(folder => (
                      <OrphanedFolderSidebarItem
                        key={folder.name}
                        folder={folder}
                        onDelete={() => setDeletingFolder(folder.name)}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div className={cn(
          'flex-1 flex flex-col min-w-0',
          mobileView === 'folders' ? 'hidden lg:flex' : 'flex'
        )}>
          {selectedFolder ? (
            <>
              {/* Panel header */}
              <div className="px-5 py-3.5 border-b border-neutral-200 dark:border-neutral-700 flex items-center gap-3 flex-shrink-0">
                {/* Mobile back */}
                <button
                  onClick={() => setMobileView('folders')}
                  className="lg:hidden p-1.5 -ml-1 rounded-lg text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>

                <div className={cn(
                  'flex-shrink-0 p-1.5 rounded-lg',
                  selectedFolder.is_admin
                    ? 'bg-blue-100 dark:bg-blue-900/30'
                    : 'bg-emerald-100 dark:bg-emerald-900/30'
                )}>
                  {selectedFolder.is_admin ? (
                    <Settings className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  ) : (
                    <Building2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                    {selectedFolder.company_name}
                  </h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {selectedFolder.media_count} file{selectedFolder.media_count !== 1 ? 's' : ''}
                  </p>
                </div>

                <FolderUploadModal folders={folders} defaultFolder={selectedFolder} inFolderView compact />
              </div>

              {/* Media content */}
              <div className="flex-1 overflow-y-auto p-5">
                <FolderSpecificMediaLibrary
                  folderId={selectedFolder.is_admin ? 'admin' : selectedFolder.id}
                  folderName={selectedFolder.company_name}
                />
              </div>
            </>
          ) : (
            /* No folder selected — empty state */
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <div className="w-20 h-20 rounded-2xl bg-neutral-100 dark:bg-neutral-700/40 flex items-center justify-center mb-5">
                <FolderOpen className="h-9 w-9 text-neutral-400 dark:text-neutral-500" />
              </div>
              <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1.5">
                Select a folder
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-[220px] leading-relaxed">
                Pick a folder from the sidebar to browse and manage its media files.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Sidebar folder item ──────────────────────────────────────────────────────

const FolderSidebarItem = ({ folder, isSelected, onClick, onDelete }) => {
  const isAdmin = folder.is_admin

  return (
    <div
      onClick={onClick}
      className={cn(
        'group flex items-center gap-2.5 px-2.5 py-2 rounded-xl cursor-pointer transition-all duration-150',
        isSelected
          ? 'bg-accent-600 shadow-sm'
          : 'hover:bg-neutral-100 dark:hover:bg-neutral-700/40'
      )}
    >
      {/* Icon */}
      <div className={cn(
        'flex-shrink-0 p-1.5 rounded-lg transition-colors',
        isSelected
          ? 'bg-white/20'
          : isAdmin
          ? 'bg-blue-100 dark:bg-blue-900/30'
          : 'bg-emerald-100 dark:bg-emerald-900/30'
      )}>
        {isAdmin ? (
          <Settings className={cn('h-3.5 w-3.5', isSelected ? 'text-white' : 'text-blue-600 dark:text-blue-400')} />
        ) : (
          <Building2 className={cn('h-3.5 w-3.5', isSelected ? 'text-white' : 'text-emerald-600 dark:text-emerald-400')} />
        )}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-sm font-medium leading-tight truncate',
          isSelected ? 'text-white' : 'text-neutral-900 dark:text-neutral-100'
        )}>
          {folder.company_name}
        </p>
        <p className={cn(
          'text-xs leading-tight mt-0.5 truncate',
          isSelected ? 'text-white/65' : 'text-neutral-400 dark:text-neutral-500'
        )}>
          {folder.media_count} {folder.media_count === 1 ? 'file' : 'files'} · {formatShortDate(folder.last_updated)}
        </p>
      </div>

      {/* Delete button (non-admin only, appears on hover/selected) */}
      {onDelete && (
        <button
          onClick={e => { e.stopPropagation(); onDelete() }}
          title="Delete folder"
          className={cn(
            'flex-shrink-0 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-all duration-150',
            isSelected
              ? 'text-white/60 hover:text-white hover:bg-white/20 opacity-60'
              : 'text-neutral-400 hover:text-error-500 hover:bg-error-50 dark:hover:bg-error-900/20'
          )}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Chevron */}
      <ChevronRight className={cn(
        'flex-shrink-0 h-3.5 w-3.5 transition-colors',
        isSelected ? 'text-white/60' : 'text-neutral-300 dark:text-neutral-600'
      )} />
    </div>
  )
}

// ─── Orphaned sidebar item ────────────────────────────────────────────────────

const OrphanedFolderSidebarItem = ({ folder, onDelete }) => (
  <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50/60 dark:bg-red-900/10">
    <div className="flex-shrink-0 p-1.5 rounded-lg bg-red-100 dark:bg-red-900/30">
      <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate text-neutral-900 dark:text-neutral-100">
        {folder.name}
      </p>
      <p className="text-xs text-red-500 dark:text-red-400">Orphaned · no client</p>
    </div>
    <button
      onClick={onDelete}
      title="Delete orphaned folder"
      className="flex-shrink-0 p-1 rounded-md text-red-400 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  </div>
)

// ─── Folder-specific media library ───────────────────────────────────────────

const FolderSpecificMediaLibrary = ({ folderId, folderName }) => {
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
  const queryClient = useQueryClient()

  const { data: mediaData, isLoading, error } = useQuery(
    ['folder-media', folderId, page, searchQuery, filterType],
    () => apiService.get(`/media/folder/${folderId}`, {
      params: { page, limit: 20, search: searchQuery, type: filterType }
    }).then(res => res.data),
    { staleTime: 5 * 60 * 1000, enabled: !!folderId }
  )

  const media = mediaData?.media || []

  const bulkDeleteMutation = useMutation(
    (fileIds) => Promise.all(fileIds.map(id => apiService.delete(`/media/${id}`))),
    {
      onSuccess: () => {
        toast.success(`${selectedFiles.length} file(s) deleted successfully`)
        queryClient.invalidateQueries(['folder-media'])
        queryClient.invalidateQueries(['media-folders'])
        setSelectedFiles([])
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Bulk delete failed')
      }
    }
  )

  const handleSelectFile = (file) => {
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.id === file.id)
      return isSelected ? prev.filter(f => f.id !== file.id) : [...prev, file]
    })
  }

  const handleSelectAll = () => {
    setSelectedFiles(selectedFiles.length === media.length ? [] : [...media])
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
          Loading {folderName} media...
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Image className="h-12 w-12 text-neutral-300 dark:text-neutral-600 mb-3" />
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
          Failed to load media
        </h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          {error.message || 'Unable to load media files'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Select all */}
          <label className="flex items-center gap-1.5 cursor-pointer select-none flex-shrink-0">
            <input
              type="checkbox"
              checked={media.length > 0 && selectedFiles.length === media.length}
              onChange={handleSelectAll}
              className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-neutral-300 rounded"
            />
            <span className="text-sm text-neutral-500 dark:text-neutral-400">All</span>
          </label>

          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg bg-neutral-50 dark:bg-dark-300 border border-neutral-200 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-600 transition-all"
            />
          </div>

          {/* Type filter */}
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="py-1.5 px-2.5 text-sm rounded-lg bg-neutral-50 dark:bg-dark-300 border border-neutral-200 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-600 transition-all flex-shrink-0"
          >
            <option value="">All types</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
            <option value="audio">Audio</option>
            <option value="document">Documents</option>
          </select>
        </div>

        {/* Bulk actions */}
        {selectedFiles.length > 0 && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm text-neutral-500 dark:text-neutral-400">
              {selectedFiles.length} selected
            </span>
            <Button
              onClick={() => setShowBulkDeleteConfirm(true)}
              variant="destructive"
              size="sm"
              disabled={bulkDeleteMutation.isLoading}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Grid */}
      {media.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
          {media.map(item => (
            <MediaItem
              key={item.id}
              item={item}
              isSelected={selectedFiles.some(f => f.id === item.id)}
              onSelect={() => handleSelectFile(item)}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-neutral-100 dark:bg-neutral-700/40 flex items-center justify-center mb-4">
            <Image className="h-7 w-7 text-neutral-400 dark:text-neutral-500" />
          </div>
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
            No media files yet
          </h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {searchQuery || filterType ? 'No files match your filters' : `Upload files to ${folderName}`}
          </p>
        </div>
      )}

      <ConfirmationModal
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={() => {
          bulkDeleteMutation.mutate(selectedFiles.map(f => f.id))
          setShowBulkDeleteConfirm(false)
        }}
        isLoading={bulkDeleteMutation.isLoading}
        title="Delete Files"
        message={
          <>
            Are you sure you want to delete{' '}
            <span className="font-medium text-neutral-900 dark:text-neutral-100">
              {selectedFiles.length} {selectedFiles.length === 1 ? 'file' : 'files'}
            </span>
            ? This cannot be undone.
          </>
        }
        confirmText="Delete Files"
      />
    </div>
  )
}

// ─── Media item ───────────────────────────────────────────────────────────────

const MediaItem = ({ item, isSelected = false, onSelect }) => {
  const [showActions, setShowActions] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const queryClient = useQueryClient()

  const isImage = item.mime_type.startsWith('image/')
  const isVideo = item.mime_type.startsWith('video/')
  const FileIcon = isImage ? Image : isVideo ? Video : File

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  const deleteMutation = useMutation(
    (id) => apiService.delete(`/media/${id}`),
    {
      onSuccess: () => {
        toast.success('Media deleted successfully')
        queryClient.invalidateQueries(['folder-media'])
        queryClient.invalidateQueries(['media-folders'])
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Delete failed')
      }
    }
  )

  const updateMutation = useMutation(
    ({ id, data }) => apiService.put(`/media/${id}`, data),
    {
      onSuccess: () => {
        toast.success('Media updated successfully')
        queryClient.invalidateQueries(['folder-media'])
        setEditModalOpen(false)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Update failed')
      }
    }
  )

  const handleEdit = (formData) => {
    updateMutation.mutate({
      id: item.id,
      data: {
        original_name: formData.get('name'),
        alt_text: formData.get('alt_text')
      }
    })
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.15 }}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        className={cn(
          'relative group cursor-pointer rounded-xl border-2 transition-all duration-200 overflow-hidden bg-white dark:bg-dark-300',
          isSelected
            ? 'border-accent-500 shadow-md shadow-accent-200/30 dark:shadow-accent-800/20'
            : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 hover:shadow-md'
        )}
      >
        {/* Preview */}
        <div className="aspect-square relative bg-neutral-50 dark:bg-dark-400 flex items-center justify-center">
          <div className="absolute top-2 left-2 z-10">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={e => { e.stopPropagation(); onSelect && onSelect() }}
              className="h-3.5 w-3.5 text-accent-600 focus:ring-accent-500 border-neutral-300 rounded"
            />
          </div>

          {isImage ? (
            <img
              src={item.thumbnail_url || item.public_url}
              alt={item.alt_text || item.original_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <FileIcon className="h-10 w-10 text-neutral-400 dark:text-neutral-500" />
          )}

          {/* Action overlay */}
          <AnimatePresence>
            {showActions && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="absolute inset-0 bg-black/55 flex items-center justify-center gap-1.5"
              >
                <button
                  onClick={e => { e.stopPropagation(); window.open(item.public_url, '_blank') }}
                  className="p-1.5 rounded-lg bg-white/90 hover:bg-white text-neutral-700 transition-colors"
                  title="View"
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={e => { e.stopPropagation(); setEditModalOpen(true) }}
                  className="p-1.5 rounded-lg bg-white/90 hover:bg-white text-neutral-700 transition-colors"
                  title="Edit"
                >
                  <Edit className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={e => { e.stopPropagation(); setShowDeleteConfirm(true) }}
                  disabled={deleteMutation.isLoading}
                  className="p-1.5 rounded-lg bg-white/90 hover:bg-white text-red-500 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {isSelected && (
            <div className="absolute top-2 right-2 z-10">
              <CheckCircle className="h-4 w-4 text-accent-600 drop-shadow" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="px-2.5 py-2">
          <p className="text-xs font-medium text-neutral-900 dark:text-neutral-100 truncate">
            {item.original_name}
          </p>
          <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-0.5">
            {formatFileSize(item.size)}
          </p>
        </div>
      </motion.div>

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div
              className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
              onClick={() => setEditModalOpen(false)}
            />
            <Card className="relative z-10 w-full max-w-md bg-white dark:bg-dark-200 shadow-2xl border-neutral-200 dark:border-neutral-700">
              <CardHeader className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                    Edit Media
                  </CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setEditModalOpen(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-4">
                <div className="space-y-5">
                  <div className="bg-neutral-50 dark:bg-dark-300 rounded-xl p-4 flex items-center justify-center">
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-white dark:bg-dark-200 flex items-center justify-center shadow-sm">
                      {isImage ? (
                        <img
                          src={item.thumbnail_url || item.public_url}
                          alt={item.alt_text || item.original_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FileIcon className="h-10 w-10 text-neutral-400" />
                      )}
                    </div>
                  </div>
                  <form
                    id="edit-media-form"
                    onSubmit={e => { e.preventDefault(); handleEdit(new FormData(e.target)) }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                        File Name
                      </label>
                      <input
                        name="name"
                        type="text"
                        defaultValue={item.original_name}
                        className="w-full px-3 py-2 text-sm rounded-lg bg-neutral-50 dark:bg-dark-300 border border-neutral-200 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-600 transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                        Alt Text
                      </label>
                      <textarea
                        name="alt_text"
                        rows={3}
                        defaultValue={item.alt_text || ''}
                        className="w-full px-3 py-2 text-sm rounded-lg bg-neutral-50 dark:bg-dark-300 border border-neutral-200 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-600 transition-all resize-none"
                        placeholder="Describe the image for accessibility..."
                      />
                    </div>
                  </form>
                </div>
              </CardContent>
              <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  form="edit-media-form"
                  disabled={updateMutation.isLoading}
                >
                  {updateMutation.isLoading ? (
                    <span className="flex items-center gap-2">
                      <LoadingSpinner size="sm" /> Saving...
                    </span>
                  ) : 'Save Changes'}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => { deleteMutation.mutate(item.id); setShowDeleteConfirm(false) }}
        isLoading={deleteMutation.isLoading}
        title="Delete File"
        message={
          <>
            Delete{' '}
            <span className="font-medium text-neutral-900 dark:text-neutral-100">
              "{item.original_name}"
            </span>
            ? This cannot be undone.
          </>
        }
        confirmText="Delete File"
      />
    </>
  )
}

// ─── Upload Modal ─────────────────────────────────────────────────────────────

const FolderUploadModal = ({ folders = [], defaultFolder = null, inFolderView = false, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedFolder, setSelectedFolder] = useState(defaultFolder)
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef(null)
  const queryClient = useQueryClient()

  const uploadMutation = useMutation(
    ({ files, clientId }) => {
      const formData = new FormData()
      files.forEach(file => formData.append('files', file))
      if (clientId) formData.append('client_id', clientId)
      return apiService.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    },
    {
      onSuccess: (data) => {
        const fileCount = data.data?.files?.length || 1
        toast.success(`${fileCount} file(s) uploaded successfully`)
        queryClient.invalidateQueries(['media-folders'])
        queryClient.invalidateQueries(['folder-media'])
        setIsOpen(false)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Upload failed')
      }
    }
  )

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files)
    if (files.length > 0) handleUpload(files)
    event.target.value = ''
  }

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setIsDragActive(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) handleUpload(files)
  }, [selectedFolder])

  const handleUpload = (files) => {
    const clientId = selectedFolder?.is_admin ? null : selectedFolder?.id
    uploadMutation.mutate({ files, clientId })
  }

  const handleDragOver = (e) => { e.preventDefault(); setIsDragActive(true) }
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragActive(false) }

  return (
    <>
      {compact ? (
        <button
          onClick={() => setIsOpen(true)}
          title="Upload files"
          className="p-1.5 rounded-lg text-neutral-500 hover:text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-900/20 transition-colors"
        >
          <Upload className="h-4 w-4" />
        </button>
      ) : (
        <Button onClick={() => setIsOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          <span>Upload Files</span>
        </Button>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen p-4">
            <div
              className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />
            <Card className="relative z-10 w-full max-w-md bg-white dark:bg-dark-200 shadow-2xl border-neutral-200 dark:border-neutral-700">
              <CardHeader className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                    Upload Media Files
                  </CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="px-6 pb-4 space-y-4">
                {/* Folder selector */}
                {!inFolderView && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                      Destination Folder
                    </label>
                    <select
                      value={selectedFolder?.id || 'admin'}
                      onChange={e => {
                        const folder = e.target.value === 'admin'
                          ? { id: null, company_name: 'Admin Media', is_admin: true }
                          : folders.find(f => f.id === e.target.value)
                        setSelectedFolder(folder)
                      }}
                      className="w-full px-3 py-2 text-sm rounded-lg bg-neutral-50 dark:bg-dark-300 border border-neutral-200 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-accent-600 transition-all"
                    >
                      <option value="admin">Admin Media</option>
                      {folders.filter(f => !f.is_admin).map(folder => (
                        <option key={folder.id} value={folder.id}>
                          {folder.company_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Uploading to label */}
                {inFolderView && selectedFolder && (
                  <div className="flex items-center gap-2 p-3 bg-neutral-50 dark:bg-dark-300 rounded-lg">
                    {selectedFolder.is_admin ? (
                      <Settings className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    ) : (
                      <Building2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    )}
                    <span className="text-sm text-neutral-700 dark:text-neutral-300">
                      Uploading to: <span className="font-medium">{selectedFolder.company_name}</span>
                    </span>
                  </div>
                )}

                {/* Drop zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200',
                    isDragActive
                      ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
                      : 'border-neutral-300 dark:border-neutral-600 hover:border-accent-400 hover:bg-neutral-50 dark:hover:bg-dark-300'
                  )}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Upload className={cn(
                    'h-10 w-10 mx-auto mb-3 transition-colors',
                    isDragActive ? 'text-accent-500' : 'text-neutral-400'
                  )} />
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 mb-1">
                    {isDragActive ? 'Drop files here!' : 'Drop files or click to browse'}
                  </p>
                  <p className="text-xs text-neutral-400 dark:text-neutral-500">
                    Images, Videos, Audio, PDFs — max 10 MB each
                  </p>
                </div>

                {uploadMutation.isLoading && (
                  <div className="flex items-center justify-center gap-2 py-2">
                    <LoadingSpinner size="sm" />
                    <span className="text-sm text-accent-600 dark:text-accent-400 font-medium">
                      Uploading...
                    </span>
                  </div>
                )}
              </CardContent>

              <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                  disabled={uploadMutation.isLoading}
                >
                  {uploadMutation.isLoading ? 'Uploading...' : 'Close'}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </>
  )
}

export default MediaFolders
